import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { defaultShouldDehydrateQuery, MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import React from "react";
import { captureQueryError } from "utils/errorReporting";
import { deserializeCache } from "./deserializeCache";
import { idbStorage } from "./idbStorage";
import { isPersistableQuery } from "./isPersistableQuery";

const STORAGE_KEY = "sidegig-marketplace-rq-cache";
const MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days

const HOME_PATH = /^\/api\/locations\/\d+\/home\//;

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: captureQueryError }),
  mutationCache: new MutationCache({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: ({ queryKey: [path] }) => typeof path === "string" && HOME_PATH.test(path),
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 10 * 1000,
      // Must be ≥ MAX_AGE: inactive entries get GC'd from memory before re-dehydration
      // otherwise, dropping persisted data on the next disk write.
      gcTime: MAX_AGE,
      retry: 1,
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: idbStorage,
  key: STORAGE_KEY,
  deserialize: deserializeCache,
});

// Persisted queries use staleTime: ∞ to skip in-session refetches, so we invalidate
// once after IDB hydration to force a fresh refetch on each page load.
const invalidatePersistedQueriesOnce = () => {
  queryClient.invalidateQueries({
    predicate: (q) => isPersistableQuery(q.queryKey),
  });
};

export const PersistedQueryClientProvider: React.FC<React.PropsWithChildren> = ({ children }) => (
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      maxAge: MAX_AGE,
      dehydrateOptions: {
        shouldDehydrateQuery: (q) => defaultShouldDehydrateQuery(q) && isPersistableQuery(q.queryKey),
      },
    }}
    onSuccess={invalidatePersistedQueriesOnce}
  >
    {children}
  </PersistQueryClientProvider>
);
